import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

/**
 * Why this exists
 * ---------------
 * `CreateEvictionComponent` refuses to advance a step, or to submit, when any control in
 * `splitForm` is invalid. That gating is intended behaviour and these helpers do not change it.
 *
 * What they add is diagnosability. When a customer reports "I can't submit your form", there is
 * nothing to investigate after the fact: the wizard makes no request when it refuses, so the
 * backend logs are empty, and the person reporting it usually cannot say which field stopped them.
 *
 * These helpers report the *shape* of the refusal — which control paths are invalid and which
 * validators failed — for analytics only. They deliberately never report control VALUES, because
 * this form carries tenant names, addresses, SSNs and dollar amounts. Error keys ('required',
 * 'min') are safe; values are not.
 *
 * Deliberately NOT included: whether a control is actually rendered on screen. Mapping a control
 * back to its DOM element needs Angular's `window.ng` debug API, which is stripped from production
 * builds — so any "is it visible" flag would silently be wrong in the only environment we care
 * about. The control path is enough to find the culprit in the template by hand.
 */

/** One invalid leaf control. `errors` holds validator KEYS only — never values. */
export interface InvalidControlReport {
  /** Dot path from the root group, e.g. `claim_details.custom_fields.beginning_date`. */
  path: string;
  /** Failing validator keys, e.g. `['required']` or `['min']`. Never any user-entered value. */
  errors: string[];
  /** True when the user has interacted with the control — an untouched blocker is the bad case. */
  touched: boolean;
}

/**
 * Walk a form tree and collect every invalid LEAF control.
 *
 * Angular excludes DISABLED controls from a parent's validity, so a disabled subtree can never be
 * the reason a submit was refused; `AbstractControl.status === 'INVALID'` already accounts for that
 * and disabled controls simply never report as invalid here.
 */
export function collectInvalidControls(root: AbstractControl | null | undefined): InvalidControlReport[] {
  const found: InvalidControlReport[] = [];
  if (!root) {
    return found;
  }

  const walk = (control: AbstractControl, path: string): void => {
    if (control instanceof FormGroup) {
      Object.entries(control.controls).forEach(([key, child]) => walk(child, path ? `${path}.${key}` : key));
      return;
    }
    if (control instanceof FormArray) {
      control.controls.forEach((child, index) => walk(child, `${path}[${index}]`));
      return;
    }
    if (control.invalid) {
      found.push({
        path,
        errors: Object.keys(control.errors ?? {}),
        touched: control.touched,
      });
    }
  };

  walk(root, '');
  return found;
}

/** Top-level group names that are invalid — the coarse "which section is at fault" signal. */
export function invalidGroupNames(root: AbstractControl | null | undefined): string[] {
  if (!(root instanceof FormGroup)) {
    return [];
  }
  return Object.entries(root.controls)
    .filter(([, control]) => control.invalid)
    .map(([name]) => name);
}
